import { getSupabaseClient } from '@/lib/supabase';
import type { ImageSearchResultJson, ImageSearchResultItem } from '@/mocks/imageSearch';
import { mockImageSearchResults } from '@/mocks/imageSearch';
import { normalizeImageForSearch } from '@/lib/imageNormalizer';

// Re-export for consumers that need image normalization (e.g. products/page.tsx)
export { normalizeImageForSearch };

// ─── Legacy Types (backward compatible) ───

export interface ImageSearchJob {
  id: string;
  user_id: string;
  company_id: string | null;
  source_image_bucket: string;
  source_image_path: string;
  job_status: 'pending' | 'processing' | 'completed' | 'failed';
  locale: string | null;
  market: string | null;
  currency: string | null;
  requested_filters: Record<string, unknown> | null;
  result_json: ImageSearchResultJson | Record<string, unknown>;
  error_message: string | null;
  error_code: string | null;
  progress_step: string | null;
  progress_percent: number | null;
  progress_message: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
}

// ─── REST API Types (Job API v1) ───

export interface ImageSearchJobApi {
  id: string;
  type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
  progress: {
    step: string | null;
    message: string | null;
    percent: number;
  };
  error: {
    code: string | null;
    message: string | null;
  } | null;
  result: Record<string, unknown> | null;
  client_request_id: string | null;
}

export interface CreateJobResponse {
  ok: boolean;
  job: ImageSearchJobApi;
  request_id: string;
}

export interface GetJobResponse {
  ok: boolean;
  job: ImageSearchJobApi;
  request_id: string;
}

// ─── File validation ───

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);
const ALLOWED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp']);
const MAX_BYTES = 10 * 1024 * 1024; // 10MB

export interface FileValidation {
  ok: boolean;
  message: string | null;
}

export function validateImageFile(file: File): FileValidation {
  const mimeType = (file.type || '').toLowerCase();
  const ext = (file.name.split('.').pop() || '').toLowerCase();

  if (!ALLOWED_TYPES.has(mimeType) && !ALLOWED_EXTENSIONS.has(ext)) {
    return { ok: false, message: 'JPG, PNG, WebP 파일만 업로드할 수 있습니다.' };
  }

  if (file.size > MAX_BYTES) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return { ok: false, message: `파일 크기는 10MB 이하여야 합니다. (현재 ${sizeMB}MB)` };
  }

  return { ok: true, message: null };
}

// ─── Auth helper ───

async function getAccessToken(): Promise<string | null> {
  const supabase = getSupabaseClient();
  if (!supabase) return null;
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

// ─── Job API base URL (absolute only — no relative fallback) ───

function requireApiBaseUrl(): string {
  const base = import.meta.env.VITE_API_BASE_URL
    .trim()
    .replace(/\/$/, '');

  if (!base) {
    throw new Error(
      'Missing VITE_API_BASE_URL. Image-search Job API requires an absolute GBCOSPACK API base URL (no relative /api fallback).',
    );
  }
  if (!/^https?:\/\//i.test(base)) {
    throw new Error(
      `VITE_API_BASE_URL must be an absolute http(s) URL (got "${base}"). Do not use relative /api paths.`,
    );
  }
  return base;
}

// ─── REST API: Create job ───

export async function createImageSearchJobApi(params: {
  jobId: string;
  sourceImageBucket: string;
  sourceImagePath: string;
  clientRequestId?: string;
  locale?: string;
  market?: string;
  currency?: string;
  requestedFilters?: Record<string, unknown>;
}): Promise<ImageSearchJobApi> {
  console.log('[image-search] 6.1 createImageSearchJobApi entered', { jobId: params.jobId });

  const token = await getAccessToken();
  if (!token) {
    console.error('[image-search] 6.1 createImageSearchJobApi FAILED: no access token');
    throw new Error('로그인이 필요합니다.');
  }

  console.log('[image-search] 6.1 access token obtained');

  const body: Record<string, unknown> = {
    id: params.jobId,
    source_image_bucket: params.sourceImageBucket,
    source_image_path: params.sourceImagePath,
  };
  if (params.clientRequestId) body.client_request_id = params.clientRequestId;
  if (params.locale) body.locale = params.locale;
  if (params.market) body.market = params.market;
  if (params.currency) body.currency = params.currency;
  if (params.requestedFilters) body.requested_filters = params.requestedFilters;

  const apiBase = requireApiBaseUrl();
  const url = `${apiBase}/api/v1/jobs/image-search`;

  console.log('[image-search] 6.3 fetch POST starting', { url, jobId: params.jobId });

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  console.log('[image-search] 6.4 fetch response received', { httpStatus: res.status, ok: res.ok });

  const responseText = await res.text();
  let responseData: Record<string, unknown> | null = null;
  try {
    responseData = JSON.parse(responseText);
  } catch {
    // response is not JSON
  }

  if (!res.ok) {
    const errorCode = (responseData as Record<string, unknown> | null)?.error
      ? ((responseData as Record<string, unknown>)?.error as Record<string, unknown>)?.code
      : null;
    const errorMessage = (responseData as Record<string, unknown> | null)?.error
      ? ((responseData as Record<string, unknown>)?.error as Record<string, unknown>)?.message
      : null;
    const requestId = (responseData as Record<string, unknown> | null)?.request_id || null;

    console.error('[image-search] 6.4 job create API error response:', {
      httpStatus: res.status,
      errorCode: errorCode || 'unknown',
      errorMessage: errorMessage || responseText,
      requestId: requestId || 'N/A',
    });

    const msg = errorMessage || `작업 생성 실패 (HTTP ${res.status})`;
    throw new Error(msg);
  }

  const data = responseData as unknown as CreateJobResponse;
  console.log('[image-search] 6.5 job create API success', { jobId: data.job.id, status: data.job.status, requestId: data.request_id });
  return data.job;
}

// ─── REST API: Poll job ───

export async function pollImageSearchJobApi(jobId: string): Promise<ImageSearchJobApi | null> {
  console.log(`[image-search] 8.2 status GET starting`, { jobId });

  const token = await getAccessToken();
  if (!token) {
    console.warn('[image-search] 8.2 status GET FAILED: no access token');
    return null;
  }

  let apiBase: string;
  try {
    apiBase = requireApiBaseUrl();
  } catch (err) {
    console.error('[image-search] 8.2 status GET FAILED: config error —', err instanceof Error ? err.message : String(err));
    return null;
  }

  const url = `${apiBase}/api/v1/jobs/image-search/${jobId}`;
  console.log(`[image-search] 8.2 status GET URL resolved`, { url });

  let res: Response;
  try {
    res = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  } catch (fetchErr) {
    console.error('[image-search] 8.3 status GET network error:', fetchErr instanceof Error ? fetchErr.message : String(fetchErr));
    return null;
  }

  console.log(`[image-search] 8.3 status GET response`, { httpStatus: res.status, ok: res.ok });

  if (!res.ok) {
    if (res.status === 404 || res.status === 401) {
      console.warn('[image-search] 8.3 status GET abort: terminal status', { jobId, httpStatus: res.status });
      return null;
    }
    const responseText = await res.text().catch(() => '');
    console.error('[image-search] 8.3 status GET error response:', { jobId, httpStatus: res.status, body: responseText.slice(0, 500) });
    return null;
  }

  let parsed: unknown;
  try {
    parsed = await res.json();
  } catch (parseErr) {
    console.error('[image-search] 8.4 status parse error:', parseErr instanceof Error ? parseErr.message : String(parseErr));
    return null;
  }

  const data = parsed as GetJobResponse;
  console.log(`[image-search] 8.4 status parsed`, {
    status: data.job.status,
    progressPercent: data.job.progress?.percent,
    progressMessage: data.job.progress?.message || '',
  });

  return data.job;
}

// ─── Poll orchestrator (recursive setTimeout, immediate first poll, max attempts) ───

export interface PollCallbacks {
  onProgress: (status: string, percent: number, message: string) => void;
  onCompleted: (items: ImageSearchResultItem[], message: string) => void;
  onFailed: (errorMessage: string) => void;
  onTimeout: () => void;
}

export function startImageSearchPolling(
  jobId: string,
  callbacks: PollCallbacks,
): { stop: () => void } {
  let timerId: ReturnType<typeof setTimeout> | null = null;
  let attempts = 0;
  const MAX_ATTEMPTS = 60;       // 60 × 2s = 2 minutes
  const POLL_INTERVAL_MS = 2000;
  let stopped = false;

  const stop = () => {
    stopped = true;
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  };

  const poll = async () => {
    if (stopped) return;

    attempts++;
    console.log(`[image-search] 8.1 poll iteration started`, { jobId, attempt: attempts });

    try {
      const apiJob = await pollImageSearchJobApi(jobId);

      if (stopped) return;

      if (!apiJob) {
        if (attempts >= MAX_ATTEMPTS) {
          console.log('[image-search] 9 timeout reached after max attempts (all polls returned null)');
          callbacks.onTimeout();
          stop();
          return;
        }
        console.log(`[image-search] 8.5 polling scheduled next (null result)`, { delayMs: POLL_INTERVAL_MS });
        timerId = setTimeout(poll, POLL_INTERVAL_MS);
        return;
      }

      // Dispatch progress
      callbacks.onProgress(apiJob.status, apiJob.progress?.percent ?? 0, apiJob.progress?.message || '');

      if (apiJob.status === 'completed') {
        console.log('[image-search] 9 job completed');
        console.log('[image-search] 10 polling stopped');
        const resultData = apiJob.result as Record<string, unknown> | null;
        const items: ImageSearchResultItem[] = (resultData?.items as ImageSearchResultItem[]) || [];
        const message = (resultData?.status_message as string) || `${items.length}개의 유사 상품을 찾았습니다.`;
        console.log('[image-search] 11 result applied', { itemCount: items.length });
        callbacks.onCompleted(items, message);
        stop();
        return;
      }

      if (apiJob.status === 'failed') {
        console.log('[image-search] 9 job failed');
        console.log('[image-search] 10 polling stopped');
        callbacks.onFailed(apiJob.error?.message || '이미지 검색에 실패했습니다.');
        stop();
        return;
      }

      // pending or processing — continue
      if (attempts >= MAX_ATTEMPTS) {
        console.log('[image-search] 9 timeout reached');
        callbacks.onTimeout();
        stop();
        return;
      }

      console.log(`[image-search] 8.5 polling scheduled next`, { delayMs: POLL_INTERVAL_MS });
      timerId = setTimeout(poll, POLL_INTERVAL_MS);
    } catch (err) {
      if (stopped) return;
      console.warn('[image-search] poll iteration unexpected error:', err instanceof Error ? err.message : String(err));
      if (attempts >= MAX_ATTEMPTS) {
        callbacks.onTimeout();
        stop();
        return;
      }
      timerId = setTimeout(poll, POLL_INTERVAL_MS);
    }
  };

  // ─── First poll is IMMEDIATE ───
  console.log('[image-search] 8 polling started', { jobId });
  poll();

  return { stop };
}

// ─── Map API job → legacy ImageSearchJob (backward compat) ───

function mapApiJobToLegacy(job: ImageSearchJobApi): ImageSearchJob {
  return {
    id: job.id,
    user_id: '',
    company_id: null,
    source_image_bucket: '',
    source_image_path: '',
    job_status: job.status,
    locale: null,
    market: null,
    currency: null,
    requested_filters: null,
    result_json: (job.result as ImageSearchResultJson) || {},
    error_message: job.error?.message || null,
    error_code: job.error?.code || null,
    progress_step: job.progress?.step || null,
    progress_percent: job.progress?.percent || 0,
    progress_message: job.progress?.message || null,
    created_at: job.created_at,
    updated_at: job.updated_at,
    started_at: job.started_at,
    completed_at: job.completed_at,
  };
}

// ─── Upload + Create Job (updated to use REST API) ───

export async function uploadAndCreateSearchJob(
  file: File,
  companyId: string | null,
): Promise<ImageSearchJob> {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Supabase not connected');

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('로그인이 필요합니다.');

  // ─── 0. 클라이언트 정규화: WebP 변환 + EXIF 보정 + 1280px 리사이즈 ───
  const normalized = await normalizeImageForSearch(file);

  const jobId = crypto.randomUUID();
  const objectPath = `image-search/${user.id}/${jobId}/source.webp`;

  // 1. Upload normalized WebP to Supabase Storage
  const { error: uploadError } = await supabase.storage
    .from('customer-image-search')
    .upload(objectPath, normalized.file, {
      contentType: 'image/webp',
      upsert: false,
    });

  if (uploadError) {
    console.error('[ImageSearch] Storage upload failed:', uploadError);
    throw new Error(`파일 업로드에 실패했습니다: ${uploadError.message}`);
  }

  // 2. Create job via REST API
  const apiJob = await createImageSearchJobApi({
    jobId,
    clientRequestId: `image-search:${jobId}`,
    sourceImageBucket: 'customer-image-search',
    sourceImagePath: objectPath,
    locale: 'ko',
    market: 'KR',
    currency: 'KRW',
    requestedFilters: { limit: 24 },
  });

  return mapApiJobToLegacy(apiJob);
}

// ─── Poll job (updated to use REST API) ───

export async function pollSearchJob(jobId: string): Promise<ImageSearchJob | null> {
  const apiJob = await pollImageSearchJobApi(jobId);
  if (!apiJob) return null;
  return mapApiJobToLegacy(apiJob);
}

// ─── Extract result items from job ───

export function getResultItems(job: ImageSearchJob): ImageSearchResultItem[] {
  const resultJson = job.result_json as ImageSearchResultJson | null;
  if (!resultJson || !resultJson.items) return [];
  return resultJson.items;
}

export function getResultMessage(job: ImageSearchJob): string {
  const resultJson = job.result_json as ImageSearchResultJson | null;
  return resultJson?.status_message || '';
}

// ─── Mock flow (when Supabase not available / for development) ───

export async function simulateImageSearch(): Promise<{
  job: ImageSearchJob;
}> {
  await new Promise((resolve) => setTimeout(resolve, 2000));

  const mockJob: ImageSearchJob = {
    id: crypto.randomUUID(),
    user_id: 'mock-user-id',
    company_id: null,
    source_image_bucket: 'customer-image-search',
    source_image_path: 'image-search/mock/source.jpg',
    job_status: 'completed',
    locale: 'ko',
    market: 'KR',
    currency: 'KRW',
    requested_filters: { limit: 24 },
    result_json: mockImageSearchResults,
    error_message: null,
    error_code: null,
    progress_step: 'completed',
    progress_percent: 100,
    progress_message: 'Search completed.',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    started_at: new Date().toISOString(),
    completed_at: new Date().toISOString(),
  };

  return { job: mockJob };
}

// ─── Supabase connectivity check ───

export async function checkSupabaseConnectivity(): Promise<boolean> {
  const supabase = getSupabaseClient();
  if (!supabase) return false;

  try {
    // Try a lightweight auth check — if Supabase is unreachable this will fail
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.warn('[ImageSearch] Supabase connectivity check failed:', error.message);
      return false;
    }
    // Even if no session exists, the fact that we got a response means Supabase is reachable
    return true;
  } catch (err) {
    console.warn('[ImageSearch] Supabase connectivity check exception:', err);
    return false;
  }
}